import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useThrottledResize } from '../hooks/useThrottledResize';

export interface TooltipProps {
  children: React.ReactElement;
  content: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  /** When false, tooltip only shows on hover (not on focus). Use for triggers that open a menu on click to avoid showing both tooltip and menu. Default true. */
  showOnFocus?: boolean;
  /**
   * Visual style. `popover` matches chart detail panels (light surface, offset below/above trigger)
   * so content does not sit on top of the trigger text.
   */
  surface?: 'default' | 'popover';
}

interface Position {
  top: number;
  left: number;
}

const TOOLTIP_VIEWPORT_PADDING = 8;
const TOOLTIP_GAP = 8;

export const Tooltip: React.FC<TooltipProps> = ({ 
  children, 
  content, 
  position = 'top',
  delay = 100,
  showOnFocus = true,
  surface = 'default',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<Position>({ top: -9999, left: -9999 });
  const [actualPosition, setActualPosition] = useState<'top' | 'bottom' | 'left' | 'right'>(position);
  const [isPositioned, setIsPositioned] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isHoveringRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  // Check if screen is lg or larger (1024px+)
  // Hover tooltips are disabled on md (768px) and below for better mobile/tablet UX
  const checkScreenSize = useCallback(() => {
    setIsLargeScreen(window.innerWidth >= 1024);
  }, []);
  
  useEffect(() => {
    // Check on mount
    checkScreenSize();
  }, [checkScreenSize]);
  
  // Use throttled resize to prevent lag
  useThrottledResize(checkScreenSize);

  const clearShowTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const clearRaf = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  /**
   * `position: fixed` uses viewport coordinates only — same idea as chart popovers
   * (see computeChartPopoverPosition). Do not add scrollX/scrollY or the panel overlaps the trigger.
   */
  const calculatePosition = useCallback((): Position | null => {
    if (!triggerRef.current || !tooltipRef.current) return null;
    // Trigger is still in the React tree but detached from layout (unmount mid-flight, or
    // hidden by a page-transition swap that keeps the old tree mounted — see the SRP/job-detail
    // island swap in server/vike-handler.ts). getBoundingClientRect() on a display:none ancestor
    // returns an all-zero rect, which would otherwise resolve to the viewport-padding fallback
    // corner (top-left) — signal "can't position this" instead of pinning it there.
    if (!triggerRef.current.isConnected) return null;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    if (triggerRect.width === 0 && triggerRect.height === 0) return null;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const p = TOOLTIP_VIEWPORT_PADDING;
    const g = TOOLTIP_GAP;

    let top = 0;
    let left = 0;
    let finalPosition = position;

    const centerX = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    const clampLeft = () =>
      Math.max(p, Math.min(centerX, vw - tooltipRect.width - p));

    switch (position) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - g;
        left = clampLeft();
        break;
      case 'bottom':
        top = triggerRect.bottom + g;
        left = clampLeft();
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - g;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + g;
        break;
    }

    // Vertical flip for top/bottom when clipped (mirror chart popover flip)
    if (position === 'top' || position === 'bottom') {
      const spaceBelow = vh - triggerRect.bottom - g;
      const spaceAbove = triggerRect.top - g;
      if (finalPosition === 'bottom' && top + tooltipRect.height > vh - p && spaceAbove > spaceBelow) {
        top = triggerRect.top - tooltipRect.height - g;
        finalPosition = 'top';
      } else if (finalPosition === 'top' && top < p && spaceBelow > spaceAbove) {
        top = triggerRect.bottom + g;
        finalPosition = 'bottom';
      }
      left = clampLeft();
    }

    // Clamp to viewport
    top = Math.max(p, Math.min(top, vh - tooltipRect.height - p));
    left = Math.max(p, Math.min(left, vw - tooltipRect.width - p));

    setActualPosition(finalPosition);
    return { top, left };
  }, [position]);

  const showTooltip = useCallback(() => {
    isHoveringRef.current = true;
    clearShowTimeout();
    timeoutRef.current = setTimeout(() => {
      if (isHoveringRef.current) {
        setIsVisible(true);
      }
    }, delay);
  }, [delay, clearShowTimeout]);

  const hideTooltip = useCallback(() => {
    isHoveringRef.current = false;
    clearShowTimeout();
    setIsVisible(false);
  }, [clearShowTimeout]);

  const updatePosition = useCallback(() => {
    clearRaf();
    rafRef.current = requestAnimationFrame(() => {
      const newPosition = calculatePosition();
      if (newPosition) {
        setTooltipPosition(newPosition);
      } else {
        // Trigger vanished or collapsed mid-flight (see calculatePosition) — hide rather than
        // leave the popover floating at a stale position (or the viewport-padding fallback).
        hideTooltip();
      }
    });
  }, [calculatePosition, clearRaf, hideTooltip]);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      setIsPositioned(false);
      // Use requestAnimationFrame to ensure tooltip is rendered and measured
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updatePosition();
          setIsPositioned(true);
        });
      });
      return () => cancelAnimationFrame(rafId);
    } else {
      setIsPositioned(false);
    }
  }, [isVisible, updatePosition]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isVisible) return;

    let ticking = false;
    const handleUpdate = () => {
      if (!ticking) {
        ticking = true;
        updatePosition();
        requestAnimationFrame(() => {
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', handleUpdate, { passive: true, capture: true });
    window.addEventListener('resize', handleUpdate, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleUpdate, { capture: true });
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isVisible, updatePosition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearShowTimeout();
      clearRaf();
    };
  }, [clearShowTimeout, clearRaf]);

  // Clone child element with event handlers
  // Only show tooltips on hover for lg+ screens (1024px+)
  // Always show on focus for accessibility
  // In React 19 ref is a regular prop (children.props.ref), not children.ref
  const existingRef = (children.props as { ref?: React.Ref<HTMLElement> })?.ref;
  const childWithRef = React.cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      if (typeof existingRef === 'function') {
        existingRef(node);
      } else if (existingRef && typeof existingRef === 'object') {
        (existingRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onMouseEnter: isLargeScreen ? (e: React.MouseEvent) => {
      showTooltip();
      children.props.onMouseEnter?.(e);
    } : children.props.onMouseEnter,
    onMouseLeave: isLargeScreen ? (e: React.MouseEvent) => {
      hideTooltip();
      children.props.onMouseLeave?.(e);
    } : children.props.onMouseLeave,
    onFocus: (e: React.FocusEvent) => {
      if (showOnFocus) showTooltip();
      if (children.props.onFocus) {
        children.props.onFocus(e);
      }
    },
    onBlur: (e: React.FocusEvent) => {
      if (showOnFocus) hideTooltip();
      if (children.props.onBlur) {
        children.props.onBlur(e);
      }
    },
  });

  const isPopoverSurface = surface === 'popover';

  const tooltipContent = isVisible ? (
    <div
      ref={tooltipRef}
      className={`fixed z-[10000] pointer-events-none ${isPopoverSurface ? 'inline-block max-w-[min(20rem,calc(100vw-1rem))]' : ''}`}
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        opacity: isPositioned ? 1 : 0,
        transition: isPositioned ? 'opacity 150ms ease-out' : 'none',
        pointerEvents: 'none',
      }}
      onMouseEnter={() => {
        // Keep tooltip visible if mouse moves to it
        isHoveringRef.current = true;
      }}
      onMouseLeave={() => {
        hideTooltip();
      }}
    >
      <div
        className={
          isPopoverSurface
            ? `relative px-3 py-2.5 rounded-xl text-sm font-normal text-[#3D3529] dark:text-[#C4A882] leading-snug
                bg-[#F8F5F0] dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520]
                shadow-xl ring-1 ring-black/5 dark:ring-white/10
                ${typeof content === 'string' ? 'whitespace-normal' : ''}`
            : `relative px-3 py-2 rounded-lg text-sm font-medium text-[#F2EADF]
                backdrop-blur-xl bg-[#1E1A14]/95 dark:bg-[#170F07]/95
                shadow-2xl dark:shadow-[0_4px_20px_rgba(0,0,0,0.45)] border border-[#4A3520]/60
                ${typeof content === 'string' ? 'whitespace-nowrap' : ''}`
        }
        style={
          isPopoverSurface
            ? undefined
            : {
                backdropFilter: 'blur(12px) saturate(180%)',
                WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                boxShadow: '0 8px 32px 0 rgba(30, 26, 20, 0.35), 0 2px 8px 0 rgba(30, 26, 20, 0.2)',
              }
        }
      >
        {content}
        {/* Arrow */}
        <div
          className={
            isPopoverSurface
              ? `absolute w-2 h-2 bg-[#F8F5F0] dark:bg-[#1F1509] border-[#DDD5C8] dark:border-[#4A3520]
                ${actualPosition === 'top' ? 'bottom-[-4px] left-1/2 -translate-x-1/2 border-b border-r rotate-45' : ''}
                ${actualPosition === 'bottom' ? 'top-[-4px] left-1/2 -translate-x-1/2 border-t border-l rotate-45' : ''}
                ${actualPosition === 'left' ? 'right-[-4px] top-1/2 -translate-y-1/2 border-b border-r rotate-45' : ''}
                ${actualPosition === 'right' ? 'left-[-4px] top-1/2 -translate-y-1/2 border-t border-l rotate-45' : ''}`
              : `absolute w-2 h-2 bg-[#1E1A14]/95 dark:bg-[#170F07]/95 border-[#4A3520]/60
                ${actualPosition === 'top' ? 'bottom-[-4px] left-1/2 -translate-x-1/2 border-b border-r rotate-45' : ''}
                ${actualPosition === 'bottom' ? 'top-[-4px] left-1/2 -translate-x-1/2 border-t border-l rotate-45' : ''}
                ${actualPosition === 'left' ? 'right-[-4px] top-1/2 -translate-y-1/2 border-b border-r rotate-45' : ''}
                ${actualPosition === 'right' ? 'left-[-4px] top-1/2 -translate-y-1/2 border-t border-l rotate-45' : ''}`
          }
        />
      </div>
    </div>
  ) : null;

  return (
    <>
      {childWithRef}
      {typeof document !== 'undefined' && createPortal(tooltipContent, document.body)}
    </>
  );
};

/**
 * Defers mounting the real Tooltip until after client hydration.
 * Use for content that is part of the initial static/SSR render (e.g. hit cards, lists).
 * Renders only the trigger child on server and first paint; tooltip behavior loads after mount.
 */
export const LazyTooltip: React.FC<TooltipProps> = (props) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) {
    return props.children;
  }
  return <Tooltip {...props}>{props.children}</Tooltip>;
};
